// src/portal-app/more/MarketingScreen.js
//
// Plan 4 Task 12 — Marketing screen, migrated to the P4 pattern (spec §5).
// See operations/TransactionsScreen.js:32-55 for the pattern this follows;
// this header only calls out decisions specific to Marketing. At 821
// pre-migration lines this is the largest P4 "More" screen.
//
// FIELD BINDING WAS WRONG — verified against the real backend before
// touching anything (the same class of defect Task 10/11 found). Traced
// every field through backend/app/routers/marketing.py,
// backend/app/routers/marketing_insights.py,
// backend/app/services/social_connection_service.py and
// backend/app/models/models.py (Promotion/PromotionResponse), and
// cross-checked against the correctly-wired web reference
// frontend/src/pages/portal/MarketingPage.js +
// frontend/src/pages/portal/marketing/PromotionsTab.js. The pre-migration
// screen (and its test fixtures, which invented a matching-but-fictional
// shape) read fields that never existed on the real payload:
//   - connections: `conn.page_name`, `conn.connected_at` — social_connection
//     _service.py's _sanitize() (lines 15-28) never returns either; the
//     real shape is {platform, external_account_id, display_name, status,
//     token_expires_at, last_checked_at, last_error, linked_ids}. `platform`
//     is never 'google'/'meta' — real values are 'google_gbp',
//     'meta_facebook', 'meta_instagram' (marketing_insights.py comment at
//     models.py:997; confirmed live at MarketingPage.js:748,827-828,933).
//   - insights: `insights.totals`, `insights.platforms` — GET /insights
//     (marketing_insights.py:80-177) returns {kpis: [...], sections:
//     {platforms: {...}}} with NO top-level totals/platforms object. `kpis`
//     is a flat array of 5-per-platform rows ({name, platform, metric,
//     value, previous_value, change_percent, invert}); the 5 real metric
//     names (reach/views/followers/total_interactions/profile_views)
//     happened to already match KPI_METRICS' keys, but summing them
//     requires filtering `kpis` by `metric`, not reading a nonexistent
//     totals object. PLATFORMS covered here is ('instagram','facebook')
//     only (marketing_insights.py:29) — Google never appears in this
//     endpoint.
//   - top posts: `post.id`, `post.timestamp` — the real fields
//     (marketing_insights.py:320-335) are `post_id` and `created_time`.
//   - reviews: `reviewsData.average_rating`, `reviewsData.total_reviews`,
//     `r.reviewer_display_name` — GET /insights/reviews
//     (marketing_insights.py:212-246) nests the aggregate under `summary`
//     ({total_review_count, average_rating, star_1..star_5} | null), and
//     the reviewer name field is `reviewer_name`. `total_reviews` as a
//     field name does not exist anywhere in the backend.
//   - promotions: `promo.id`, `promo.type`, `promo.usage_count` — the real
//     PromotionResponse (models.py:2102-2109, PromotionBase:2078-2095) has
//     `promotion_id` and `rule_type` (enum: order_percentage, order_fixed,
//     item_percentage, item_fixed, bogo, combo — never
//     percentage_discount/fixed_discount/bogo/bundle). There is NO
//     usage-tracking column on the promotions table at all —
//     promotion_service.list_promotions is a plain unfiltered query
//     (promotion_service.py:6-7); `usage_count` had no data source and
//     always rendered from `undefined`.
// Every field below is now bound to the verified real shape. This is not a
// behaviour change so much as the screen's already-declared intent (show
// real interactions/reviews/promotions) finally being reachable.
//
// GATING — read before touching anything here. This screen is built
// against the Google Business Profile API, which was still pending
// approval as of 2026-06-29 (see project memory). Every gate below is
// preserved exactly, none loosened:
//   - Reviews are Google-only: `hasGoogle` now checks the REAL fields
//     (platform === 'google_gbp' && status === 'connected' — matching
//     MarketingPage.js:748's identical check) instead of the fictional
//     `platform === 'google'`. This is a field-name fix, not a gate
//     change — the gate's INTENT (Google-connected tenants only) is
//     unchanged, and mutation-tested below (see task-12-report.md).
//   - Feature/permission gating for the whole Marketing surface happens
//     one layer up, at the More-hub card (nav/tabs.js:89 — permission
//     'portal:marketing', feature 'marketing' — enforced in
//     nav/useHubCards.js). This screen has never checked either itself
//     (grep confirms zero hasFeature/hasPermission calls in the
//     pre-migration file, and the equivalent web page MarketingPage.js
//     also has none), which matches the DevicesScreen precedent (Task 11)
//     for the same hub. Not adding a screen-level check here preserves
//     that existing separation of concerns.
//   - GBP not being approved yet naturally means `connections` returns
//     empty/without a google_gbp row for every tenant right now — the
//     SocialEmpty placeholder and the collapsed reviews section ARE the
//     correct rendering of that state, not a bug to "fix" into fabricated
//     data.
//
// Two segments (Social | Promotions) — a genuine sibling-view switch, not
// a categorical filter in disguise (Plan 4 review lesson 2): they hit
// completely different services (marketingService vs promotionsService),
// return different shapes, and load independently (promotions never
// depends on the date range; social insights never depend on promotions).
// SegmentedControl kept, now imported from ../shell/kit per Task 1's move.
//
// RangeBar kept — Social's only real control. No FilterButton/FilterSheet:
// neither posts, reviews nor promotions had (or need) a categorical filter
// in the pre-migration screen; adding one here would be inventing new
// scope the brief never asked for.
//
// HERO — "Total Interactions" for the selected range (brief: "total
// interactions or reviews... verify names"; total_interactions is real,
// total_reviews is not — see above). Chosen over reviews because it is
// always meaningful for ANY connected tenant (Meta-only tenants have no
// reviews at all, since GBP isn't live), and it is summed via `sumMetric`
// from the SAME date-filtered /insights fetch that also powers the KPI
// grid and, via the same `range` state, the top-posts list below it — the
// same "hero summarises a period total, list below shows a paginated/
// truncated subset of the SAME filtered fetch" relationship
// TransactionsScreen's revenue hero already established (its hero is a
// period total, not a literal sum of the visible page of rows either).
// Outside SurfaceStates (gated on `status === 'ready' && connections.length
// > 0`, not on topPosts/reviews being empty) so it survives an empty top-
// posts/reviews result for the chosen range — mutation-tested below.
//
// Promotions gets its own hero (DevicesScreen precedent: value = total
// registered, caption = active count in WORDS). Not in the original
// screen, but the P4 recipe's hero zone is not optional, and unlike
// Social's period total, Promotions' hero denominator is EXACT — the list
// below is never filtered, so total === rows.length in every state.
//
// Rows — CondensedRow for all three lists (posts, reviews, promotions).
// Two-badge originals collapsed to one pill each (CondensedRow has a
// single pill slot):
//   posts       — pill = media type (changes per post, more informative
//                 here than platform, which the Connections section above
//                 already states); platform badge dropped, disclosed.
//                 name = caption excerpt, value = reach, secondary = date
//                 + likes/comments/shares (all 4 original stats kept, none
//                 dropped — reach moved to the value slot instead of being
//                 silently lost).
//   reviews     — pill = star count as a WORD ("5 Stars", tone by rating —
//                 colour is never the sole carrier, spec §9). name =
//                 comment excerpt (falls back to a translated placeholder
//                 when the comment is empty — comment is nullable on the
//                 real model). secondary = reviewer_name + date (both kept,
//                 neither dropped). The header block above the rows (avg
//                 rating number + SVG stars + count) is unchanged from the
//                 original — still inline SVG, never emoji.
//   promotions  — NOT called out by name in the brief's row bullet, but
//                 it's a flat, unfiltered, un-nested list — the exact
//                 shape CondensedRow exists for (unlike Voids' parent/
//                 child nesting, which is why TransactionsScreen exempted
//                 it). pill = enabled/disabled (WORD + tone). Rule type
//                 (dropped from a badge) moves into secondary next to the
//                 schedule dates — nothing lost, just relocated to text.
//                 usage_count is gone because it never existed; schedule
//                 dates (start_date/end_date, both real fields) replace it
//                 as secondary text — the ORIGINAL mobile screen already
//                 showed exactly these two dates (mkt-promo-dates), so
//                 nothing new is being surfaced, just relocated.
//
// Content bug fixed, disclosed: the pre-migration PromotionsTab's empty
// state rendered <DesktopBanner/> — "Connect your Meta or Google Business
// Profile from the web portal" — which has zero relevance to promotions
// (created via the web portal by any manager, independent of any social
// connection; there is no promotions OAuth flow). This reads as a
// copy-paste from SocialEmpty, not an intentional design choice, and is
// removed from the Promotions empty state only; SocialEmpty (where the
// banner IS relevant) is unchanged.
//
// KPI grid — kept (not in the P4 recipe's minimal shape, but dropping five
// real, distinct metrics — reach/views/followers/interactions/profile
// views — down to the one hero figure would be a real information loss
// the brief never asked for). Per-platform breakdown text only renders
// when 2+ Meta platforms are actually CONNECTED (status: 'connected'),
// not merely present in `sections.platforms` (which — per
// marketing_insights.py:132 — always has both 'instagram' and 'facebook'
// keys regardless of connection state, since PLATFORMS is a fixed tuple).
// Showing "Instagram: 0 · Facebook: 1,204" for a Facebook-only tenant
// would misrepresent an unconnected platform as legitimately zero.
//
// Connections section — NOT migrated to CondensedRow (the brief's row
// bullet scopes CondensedRow to "review/post"; connections is a short,
// unfiltered account list, not itself a paginated/filtered list). Kept as
// a GlassCard row, re-bound to the real fields: display_name (page_name
// dropped, disclosed — the field doesn't exist), platform label (now
// distinguishes Instagram from Facebook, which the old 'meta' bucket
// couldn't), and status badge (connected/expired/pending_location/error —
// all four real backend statuses, not inferred). connected_at is dropped
// (field doesn't exist); last_checked_at (a real field) replaces it where
// present.
//
// PullToRefresh (Task 2) wraps the count-line + rows body for both
// segments; onRefresh calls loadConnections() directly. No extra chained
// call to loadInsights is needed: loadConnections's status transition
// ready → loading → ready re-triggers the existing insights-loading effect
// (dependency array includes `status`), which picks up the CURRENT `range`
// from its own closure — verified in the effect wiring below.
//
// Stale-response guard (seqRef): unchanged — increments on every
// loadInsights() call; only the response whose seq still matches when it
// resolves is applied.
//
// This screen renders CONTENT ONLY — NavStackHost owns the header.
import React, {
    useState, useEffect, useCallback, useRef, useMemo,
} from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { marketingService, promotionsService } from '../../services/api';
import { SurfaceStates, RangeBar } from '../insights/kit';
import {
    SegmentedControl,
    StatusBadge,
    CondensedRow,
    HeroStat,
    PullToRefresh,
    ensureKitCss,
    IconTag,
} from '../shell/kit';
import { GlassCard } from '../shell/LiquidGlass';
import { useNavChrome } from '../nav/NavStack';
import { rangeForPreset, toLocalISO } from '../nav/dateRange';

/* ------------------------------------------------------------------ */
/* Screen-local stylesheet                                              */
/* ------------------------------------------------------------------ */
const CSS = `
/* ---- layout ---- */
.mkt-wrap{width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;
  padding:8px 0 80px;}

/* ---- segments ---- */
.mkt-seg{padding:0 16px 12px;}

/* ---- hero (both segments share this shell around <HeroStat/>) ---- */
.mkt-hero{margin:0 16px 16px;padding:18px 20px 16px;}

/* ---- desktop banner (Social empty state only) ---- */
.mkt-banner{
  margin:0 16px 16px;padding:14px 16px;border-radius:16px;
  display:flex;align-items:flex-start;gap:12px;
  background:color-mix(in srgb,var(--c-blue) 8%,transparent);
  border:1px solid color-mix(in srgb,var(--c-blue) 20%,transparent);}
.mkt-banner-icon{flex-shrink:0;margin-top:1px;}
.mkt-banner-text{display:flex;flex-direction:column;gap:2px;}
.mkt-banner-title{font-size:13px;font-weight:700;color:var(--c-blue);}
.mkt-banner-sub{font-size:12px;color:var(--c-textLight);line-height:1.4;}

/* ---- section padding ---- */
.mkt-section{padding:0 16px;margin-bottom:12px;}
.mkt-section-title{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:.8px;color:var(--c-textLight);margin-bottom:8px;}

/* ---- count line ---- */
.mkt-count-line{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:1.2px;color:var(--c-textLight);margin:2px 16px 10px;}

/* ---- connection cards ---- */
.mkt-conn-list{display:flex;flex-direction:column;gap:8px;}
.mkt-conn-card{padding:14px 16px;display:flex;align-items:center;gap:12px;}
.mkt-conn-icon{width:36px;height:36px;border-radius:10px;flex-shrink:0;
  display:flex;align-items:center;justify-content:center;
  background:color-mix(in srgb,var(--c-surface) 40%,transparent);}
.mkt-conn-info{flex:1;min-width:0;}
.mkt-conn-name{font-size:13px;font-weight:700;color:var(--c-ink);
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.mkt-conn-date{font-size:11px;font-weight:500;color:var(--c-textLight);margin-top:2px;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}

/* ---- rangebar wrapper ---- */
.mkt-range{padding:4px 16px 0;}

/* ---- KPI grid ---- */
.mkt-kpi-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
.mkt-kpi-card{padding:14px 15px;box-sizing:border-box;}
.mkt-kpi-label{font-size:10.5px;font-weight:600;letter-spacing:.6px;
  text-transform:uppercase;color:var(--c-textLight);margin-bottom:6px;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.mkt-kpi-value{font-size:22px;font-weight:800;letter-spacing:-.6px;
  color:var(--c-ink);line-height:1.15;
  font-variant-numeric:tabular-nums;}
.mkt-kpi-breakdown{margin-top:6px;font-size:11px;font-weight:500;
  color:var(--c-textLight);line-height:1.4;}

/* ---- posts / reviews / promotions lists ---- */
.mkt-posts-list,.mkt-reviews-list,.mkt-promo-list{display:flex;flex-direction:column;gap:8px;}

/* ---- reviews average-rating header ---- */
.mkt-reviews-header{display:flex;align-items:center;gap:10px;margin-bottom:8px;}
.mkt-rating-avg{font-size:28px;font-weight:800;color:var(--c-ink);
  font-variant-numeric:tabular-nums;letter-spacing:-.5px;}
.mkt-rating-meta{display:flex;flex-direction:column;gap:2px;}
.mkt-rating-stars{display:flex;gap:2px;align-items:center;}
.mkt-rating-count{font-size:12px;font-weight:500;color:var(--c-textLight);}

/* ---- empty states ---- */
.mkt-empty{padding:40px 24px;text-align:center;
  display:flex;flex-direction:column;align-items:center;gap:12px;}
.mkt-empty-icon{width:48px;height:48px;border-radius:14px;
  display:flex;align-items:center;justify-content:center;
  background:color-mix(in srgb,var(--c-green) 10%,transparent);
  color:var(--c-green);}
.mkt-empty-icon svg{width:24px;height:24px;}
.mkt-empty-title{font-size:15px;font-weight:700;color:var(--c-ink);}
.mkt-empty-sub{font-size:13px;font-weight:500;color:var(--c-textLight);
  line-height:1.5;max-width:280px;}
`;

let _cssInjected = false;
function ensureCss() {
    if (_cssInjected) return;
    const el = document.createElement('style');
    el.textContent = CSS;
    document.head.appendChild(el);
    _cssInjected = true;
}

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */
// Real /insights KPI `metric` values (marketing_insights.py:77,155-164) —
// verified against the router, not assumed.
const KPI_METRICS = [
    { key: 'reach',              labelKey: 'reach',             fallback: 'Reach' },
    { key: 'views',              labelKey: 'views',             fallback: 'Views' },
    { key: 'followers',          labelKey: 'followers',         fallback: 'Followers' },
    { key: 'total_interactions', labelKey: 'total_interactions', fallback: 'Interactions' },
    { key: 'profile_views',      labelKey: 'profile_views',     fallback: 'Profile Views' },
];

// Real PromotionResponse.rule_type values (models.py:636 comment) — never
// the fictional percentage_discount/fixed_discount/bogo/bundle the
// pre-migration screen assumed.
const RULE_TONE = {
    order_percentage: 'blue',
    item_percentage:  'blue',
    order_fixed:      'green',
    item_fixed:       'green',
    bogo:             'amber',
    combo:            'purple',
};
const RULE_FALLBACK = {
    order_percentage: 'Percentage Off Order',
    order_fixed:      'Fixed Amount Off Order',
    item_percentage:  'Percentage Off Item',
    item_fixed:       'Fixed Amount Off Item',
    bogo:             'Buy One Get One',
    combo:            'Combo Deal',
};

// Maps media_type enum → translation key
const MEDIA_TYPE_KEYS = {
    IMAGE:         'media_image',
    VIDEO:         'media_video',
    CAROUSEL_ALBUM:'media_carousel',
    REELS:         'media_reel',
};

// Maps Google star_rating string → numeric 1–5
const STAR_VALUE = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };

// Real TenantSocialConnection.platform values — never 'google'/'meta'.
const PLATFORM_LABEL = {
    google_gbp:     ['platform_google_gbp', 'Google Business Profile'],
    meta_facebook:  ['platform_meta_facebook', 'Facebook'],
    meta_instagram: ['platform_meta_instagram', 'Instagram'],
};

// Real TenantSocialConnection.status values.
const CONNECTION_STATUS_TONE = {
    connected:        'green',
    expired:          'amber',
    pending_location: 'blue',
    error:            'red',
};

const DEFAULT_PRESET = 'week';

/* ------------------------------------------------------------------ */
/* Inline SVG helpers — never emoji.                                   */
/* ------------------------------------------------------------------ */
const IconInfo = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        aria-hidden="true">
        <circle cx="12" cy="12" r="10"/>
        <path d="M12 8v4"/><path d="M12 16h.01"/>
    </svg>
);

const IconMarketing = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        aria-hidden="true">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
    </svg>
);

const IconMeta = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
        aria-hidden="true">
        <path d="M2 12c0-3 1.5-6 4-6 2 0 3 2 4 4s2.5 5 4.5 5 3.5-3 3.5-5-1-4-3-4"/>
    </svg>
);

const IconGoogle = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
);

/* ------------------------------------------------------------------ */
/* StarRating — renders N filled / unfilled stars via inline SVG.      */
/* NEVER use emoji for stars.                                          */
/* ------------------------------------------------------------------ */
function StarRating({ rating, size = 14 }) {
    const full = Math.floor(rating);
    const empty = 5 - full;
    const stars = [
        ...Array.from({ length: full }, (_, i) => ({ key: `f${i}`, filled: true })),
        ...Array.from({ length: empty }, (_, i) => ({ key: `e${i}`, filled: false })),
    ];
    return (
        <span style={{ display: 'inline-flex', gap: 2, alignItems: 'center' }}>
            {stars.map(({ key, filled }) => (
                <svg
                    key={key}
                    width={size}
                    height={size}
                    viewBox="0 0 24 24"
                    fill={filled ? 'var(--c-amber)' : 'none'}
                    stroke="var(--c-amber)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                </svg>
            ))}
        </span>
    );
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */
function fmtNum(v) {
    return Number(v || 0).toLocaleString();
}

function fmtDate(isoStr) {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    if (Number.isNaN(d.getTime())) return isoStr;
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

// Sums a real /insights kpi metric across every platform row that carries
// it (marketing_insights.py:155-164 — kpis is a flat 5-per-platform array,
// there is no pre-aggregated total anywhere in the response).
function sumMetric(kpis, metric) {
    return (kpis || [])
        .filter((k) => k.metric === metric)
        .reduce((s, k) => s + (Number(k.value) || 0), 0);
}

function metricByPlatform(kpis, metric, platform) {
    const k = (kpis || []).find((x) => x.metric === metric && x.platform === platform);
    return k ? (Number(k.value) || 0) : 0;
}

function platformLabel(platform, t) {
    const entry = PLATFORM_LABEL[platform];
    if (!entry) return platform || '';
    return t(entry[0], {}, entry[1]);
}

// insights `platform` values are bare 'instagram'/'facebook' (no 'meta_'
// prefix, no google) — distinct from connections' platform vocabulary.
// Reuses the same wording via the connections-style keys above.
function platformShortLabel(barePlatform, t) {
    return barePlatform === 'instagram'
        ? t('platform_meta_instagram', {}, 'Instagram')
        : t('platform_meta_facebook', {}, 'Facebook');
}

function connectionIcon(platform) {
    return platform === 'google_gbp' ? <IconGoogle /> : <IconMeta />;
}

function connectionStatusLabel(status, t) {
    if (status === 'connected') return t('connection_status_connected', {}, 'Connected');
    if (status === 'expired') return t('connection_status_expired', {}, 'Expired');
    if (status === 'pending_location') return t('connection_status_pending_location', {}, 'Needs Setup');
    if (status === 'error') return t('connection_status_error', {}, 'Connection Error');
    return status || '';
}

function starTone(count) {
    if (count >= 4) return 'green';
    if (count === 3) return 'amber';
    if (count >= 1) return 'red';
    return 'muted';
}

// Promotions' schedule dates — the ORIGINAL mobile screen already showed
// exactly these two real fields (mkt-promo-dates); relocated to secondary
// text instead of a separate footer, nothing new invented.
function scheduleLabel(promo) {
    if (!promo.start_date && !promo.end_date) return null;
    const s = promo.start_date ? fmtDate(`${promo.start_date}T12:00:00`) : '';
    const e = promo.end_date ? fmtDate(`${promo.end_date}T12:00:00`) : '';
    if (s && e) return `${s} – ${e}`;
    return s || e;
}

function ruleTypeLabel(ruleType, t) {
    return t(`rule_${ruleType}`, {}, RULE_FALLBACK[ruleType] || ruleType || '');
}

/* ------------------------------------------------------------------ */
/* DesktopBanner — informational "connect from desktop" notice.        */
/* Social empty state ONLY — see file header for why it was removed    */
/* from the Promotions empty state.                                    */
/* ------------------------------------------------------------------ */
function DesktopBanner({ t }) {
    return (
        <div className="mkt-banner">
            <span className="mkt-banner-icon"><IconInfo /></span>
            <div className="mkt-banner-text">
                <span className="mkt-banner-title">
                    {t('connect_from_desktop', {}, 'Connect from the web portal')}
                </span>
                <span className="mkt-banner-sub">
                    {t('connect_accounts_desktop_sub', {}, 'Connecting social accounts requires the desktop web portal. Visit your portal to link Meta or Google Business Profile.')}
                </span>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* SocialEmpty — shown when no platforms are connected                 */
/* ------------------------------------------------------------------ */
function SocialEmpty({ t }) {
    return (
        <>
            <DesktopBanner t={t} />
            <div className="mkt-section">
                <div className="mkt-empty">
                    <div className="mkt-empty-icon">
                        <IconMarketing />
                    </div>
                    <div className="mkt-empty-title">
                        {t('no_connections', {}, 'No accounts connected')}
                    </div>
                    <div className="mkt-empty-sub">
                        {t('connect_accounts', {}, 'Connect your Meta or Google Business Profile from the web portal to see insights here.')}
                    </div>
                </div>
            </div>
        </>
    );
}

/* ------------------------------------------------------------------ */
/* ConnectionCard — kept as a custom GlassCard row (not CondensedRow;   */
/* see file header). Re-bound to the real field set.                   */
/* ------------------------------------------------------------------ */
function ConnectionCard({ conn, t }) {
    const label = platformLabel(conn.platform, t);
    return (
        <GlassCard className="mkt-conn-card">
            <div className="mkt-conn-icon">
                {connectionIcon(conn.platform)}
            </div>
            <div className="mkt-conn-info">
                <div className="mkt-conn-name">{conn.display_name || label}</div>
                <div className="mkt-conn-date">
                    {label}
                    {conn.last_checked_at
                        ? ` · ${t('last_synced', {}, 'Synced')} ${fmtDate(conn.last_checked_at)}`
                        : ''}
                </div>
            </div>
            <StatusBadge
                status={conn.status}
                label={connectionStatusLabel(conn.status, t)}
                tone={CONNECTION_STATUS_TONE[conn.status] || 'muted'}
            />
        </GlassCard>
    );
}

/* ------------------------------------------------------------------ */
/* KpiGrid — 5 tiles in a 2-up grid, summed from the real kpis[] array. */
/* ------------------------------------------------------------------ */
function KpiGrid({ kpis, connectedPlatforms, t }) {
    const showBreakdown = connectedPlatforms.length > 1;
    return (
        <div className="mkt-kpi-grid">
            {KPI_METRICS.map(({ key, labelKey, fallback }) => {
                const total = sumMetric(kpis, key);
                return (
                    <GlassCard
                        key={key}
                        className="mkt-kpi-card"
                        data-testid="kpi-card"
                    >
                        <div className="mkt-kpi-label">
                            {t(labelKey, {}, fallback)}
                        </div>
                        <div className="mkt-kpi-value">
                            {fmtNum(total)}
                        </div>
                        {showBreakdown && (
                            <div className="mkt-kpi-breakdown">
                                {connectedPlatforms.map((p) => (
                                    <span key={p} style={{ marginInlineEnd: 8 }}>
                                        {platformShortLabel(p, t)}: {fmtNum(metricByPlatform(kpis, key, p))}
                                    </span>
                                ))}
                            </div>
                        )}
                    </GlassCard>
                );
            })}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* TopPostRow — CondensedRow. name=caption excerpt, value=reach,        */
/* secondary=date + likes/comments/shares, pill=media type.             */
/* ------------------------------------------------------------------ */
function TopPostRow({ post, t }) {
    const mediaKey = MEDIA_TYPE_KEYS[post.media_type] || MEDIA_TYPE_KEYS[String(post.media_type || '').toUpperCase()];
    const mediaLabel = mediaKey ? t(mediaKey, {}, post.media_type) : (post.media_type || '');
    const name = post.caption || t('no_caption', {}, '(No caption)');
    const engagement = [
        `${fmtNum(post.likes)} ${t('likes', {}, 'Likes')}`,
        `${fmtNum(post.comments)} ${t('comments', {}, 'Comments')}`,
        `${fmtNum(post.shares)} ${t('shares', {}, 'Shares')}`,
    ].join(' · ');
    const secondary = [fmtDate(post.created_time), engagement].filter(Boolean).join(' · ');

    return (
        <CondensedRow
            icon={<IconMeta />}
            tone="blue"
            name={name}
            value={fmtNum(post.reach)}
            secondary={secondary}
            pill={mediaLabel ? { label: mediaLabel, tone: 'muted' } : undefined}
            testId={post.post_id}
        />
    );
}

/* ------------------------------------------------------------------ */
/* ReviewRow — CondensedRow. name=comment excerpt, secondary=reviewer   */
/* + date, pill=star count as a WORD (colour is never the sole carrier  */
/* of meaning).                                                        */
/* ------------------------------------------------------------------ */
function ReviewRow({ review, t }) {
    const starCount = STAR_VALUE[review.star_rating] || 0;
    const name = review.comment || t('no_review_comment', {}, 'No comment left');
    const secondary = [review.reviewer_name, fmtDate(review.create_time)].filter(Boolean).join(' · ');

    return (
        <CondensedRow
            icon={<IconGoogle />}
            tone="green"
            name={name}
            secondary={secondary || undefined}
            pill={{ label: t('n_stars', { count: starCount }, '{count} Stars'), tone: starTone(starCount) }}
            testId={review.review_id}
        />
    );
}

/* ------------------------------------------------------------------ */
/* ReviewsSection — shown only when Google is among the connections    */
/* (gated by `hasGoogle` in the parent). `hasReviews` derives from the  */
/* REAL `summary.total_review_count` — summary is null when there are   */
/* no reviews at all, per marketing_insights.py's aggregate query.      */
/* ------------------------------------------------------------------ */
function ReviewsSection({ reviewsData, t }) {
    const summary = reviewsData.summary;
    const hasReviews = !!(summary && Number(summary.total_review_count) > 0);

    if (!hasReviews) {
        return (
            <div data-testid="reviews-section">
                <div className="mkt-empty">
                    <div className="mkt-empty-title">{t('no_reviews', {}, 'No reviews yet')}</div>
                </div>
            </div>
        );
    }

    const reviews = reviewsData.reviews || [];
    return (
        <div data-testid="reviews-section">
            <div className="mkt-reviews-header">
                <div className="mkt-rating-avg">{Number(summary.average_rating || 0).toFixed(1)}</div>
                <div className="mkt-rating-meta">
                    <div className="mkt-rating-stars">
                        <StarRating rating={summary.average_rating || 0} size={16} />
                    </div>
                    <div className="mkt-rating-count">
                        {fmtNum(summary.total_review_count)} {t('total_reviews', {}, 'reviews')}
                    </div>
                </div>
            </div>

            <div className="mkt-reviews-list">
                {reviews.map((r) => (
                    <ReviewRow key={r.review_id} review={r} t={t} />
                ))}
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* PromotionRow — CondensedRow. pill=enabled/disabled, secondary=rule   */
/* type + schedule dates, icon tone by rule-type bucket.                */
/* ------------------------------------------------------------------ */
function PromotionRow({ promo, t }) {
    const tone = RULE_TONE[promo.rule_type] || 'muted';
    const secondary = [ruleTypeLabel(promo.rule_type, t), scheduleLabel(promo)].filter(Boolean).join(' · ');

    return (
        <CondensedRow
            icon={<IconTag />}
            tone={tone}
            name={promo.name}
            secondary={secondary || undefined}
            pill={{
                label: promo.enabled ? t('promotion_enabled', {}, 'Active') : t('promotion_disabled', {}, 'Inactive'),
                tone: promo.enabled ? 'green' : 'muted',
            }}
            testId={promo.promotion_id}
        />
    );
}

/* ------------------------------------------------------------------ */
/* PromotionsBody — count line + CondensedRow list, or empty state.     */
/* No DesktopBanner here — see file header for why it was removed.      */
/* ------------------------------------------------------------------ */
function PromotionsBody({ promotions, t }) {
    if (promotions.length === 0) {
        return (
            <div className="mkt-section">
                <div className="mkt-empty" data-testid="promos-empty">
                    <div className="mkt-empty-icon">
                        <IconTag />
                    </div>
                    <div className="mkt-empty-title">
                        {t('no_promotions', {}, 'No promotions configured')}
                    </div>
                    <div className="mkt-empty-sub">
                        {t('no_promotions_sub', {}, 'Create promotions from the web portal to see them here.')}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className="mkt-count-line" data-testid="promos-count-line">
                {t('n_promotions', { count: promotions.length }, '{count} Promotions')}
            </div>
            <div className="mkt-section">
                <div className="mkt-promo-list">
                    {promotions.map((promo) => (
                        <PromotionRow key={promo.promotion_id} promo={promo} t={t} />
                    ))}
                </div>
            </div>
        </>
    );
}

/* ================================================================== */
/* MarketingScreen — main component                                     */
/* ================================================================== */
export default function MarketingScreen() {
    ensureCss();
    ensureKitCss();
    useNavChrome();
    const { t } = useLanguage();

    // ── segment — genuine sibling-view switch, see file header ──────────
    const [segment, setSegment] = useState('social');

    // ── connections + promotions (one combined initial fetch) ───────────
    const [status, setStatus] = useState('loading');
    const [connections, setConnections] = useState([]);
    const [promotions, setPromotions] = useState([]);

    // ── social insights (Social tab only) ────────────────────────────────
    const [insights, setInsights] = useState(null);
    const [topPosts, setTopPosts] = useState([]);
    const [reviews, setReviews] = useState(null);

    // ── date range ────────────────────────────────────────────────────────
    const [preset, setPreset] = useState(DEFAULT_PRESET);
    const [range, setRange] = useState(() => rangeForPreset(DEFAULT_PRESET));

    // ── stale-response guard ──────────────────────────────────────────────
    const seqRef = useRef(0);

    // ── load connections + promotions ────────────────────────────────────
    const loadConnections = useCallback(async () => {
        setStatus('loading');
        try {
            const [conns, promos] = await Promise.all([
                marketingService.getConnections(),
                promotionsService.getAll(),
            ]);
            setConnections(conns || []);
            setPromotions(promos || []);
            setStatus('ready');
        } catch (err) {
            setStatus('error');
        }
    }, []);

    useEffect(() => {
        loadConnections();
    }, [loadConnections]);

    // ── load insights when connections or range changes ────────────────────
    const loadInsights = useCallback(async (start, end, conns) => {
        if (!conns || conns.length === 0) return;

        seqRef.current += 1;
        const seq = seqRef.current;

        const startStr = toLocalISO(start);
        const endStr = toLocalISO(end);

        try {
            // Gate fix, not a gate change (see file header): real field names,
            // same intent as the pre-migration `platform === 'google'` check.
            const hasGoogleNow = conns.some((c) => c.platform === 'google_gbp' && c.status === 'connected');

            const fetches = [
                marketingService.getInsights(startStr, endStr),
                marketingService.getTopPosts(startStr, endStr, null, 10),
                hasGoogleNow ? marketingService.getReviews(10) : Promise.resolve(null),
            ];

            const [insightsData, postsData, reviewsData] = await Promise.all(fetches);

            // Stale-response guard: discard if a newer fetch has started
            if (seq !== seqRef.current) return;

            setInsights(insightsData);
            setTopPosts((postsData && postsData.posts) || []);
            setReviews(reviewsData);
        } catch (err) {
            // Insights failure is non-fatal: connections still show
            if (seq !== seqRef.current) return;
            setInsights(null);
            setTopPosts([]);
            setReviews(null);
        }
    }, []);

    // Load insights when connections resolve (and are non-empty). Also the
    // vehicle for pull-to-refresh: loadConnections's ready→loading→ready
    // transition reruns this effect (status is a dependency), picking up
    // the CURRENT `range` from this closure — no extra chained call needed.
    useEffect(() => {
        if (status === 'ready' && connections.length > 0) {
            loadInsights(range.start, range.end, connections);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [status, connections.length, loadInsights]);

    // Reload insights when range changes (and connections present)
    const handleRangeChange = useCallback((id, newRange) => {
        setPreset(id);
        setRange(newRange);
        if (connections.length > 0) {
            loadInsights(newRange.start, newRange.end, connections);
        }
    }, [connections, loadInsights]);

    // ── segments ──────────────────────────────────────────────────────────
    const SEG_OPTIONS = [
        { value: 'social',     label: t('social',     {}, 'Social') },
        { value: 'promotions', label: t('promotions', {}, 'Promotions') },
    ];

    // Google-only reviews gate — real fields, same intent as before.
    const hasGoogle = useMemo(
        () => connections.some((c) => c.platform === 'google_gbp' && c.status === 'connected'),
        [connections],
    );

    // Connected Meta platforms (bare 'instagram'/'facebook', matching
    // /insights' vocabulary) — drives the KPI breakdown gate, see header.
    const connectedMetaPlatforms = useMemo(
        () => connections
            .filter((c) => c.status === 'connected' && (c.platform === 'meta_instagram' || c.platform === 'meta_facebook'))
            .map((c) => (c.platform === 'meta_instagram' ? 'instagram' : 'facebook')),
        [connections],
    );

    const totalInteractions = useMemo(
        () => sumMetric(insights ? insights.kpis : null, 'total_interactions'),
        [insights],
    );

    const activePromoCount = useMemo(
        () => promotions.filter((p) => p.enabled).length,
        [promotions],
    );

    const canShowSocialHero = segment === 'social' && status === 'ready' && connections.length > 0;
    const canShowPromotionsHero = segment === 'promotions' && status === 'ready';

    return (
        <div className="mkt-wrap">
            {/* Segmented control — always visible, a genuine sibling-view
                switch (see file header). */}
            <div className="mkt-seg">
                <SegmentedControl
                    options={SEG_OPTIONS}
                    value={segment}
                    onChange={setSegment}
                />
            </div>

            {/* Social hero + RangeBar + KPI grid — OUTSIDE SurfaceStates so
                they survive an empty top-posts/reviews result for the
                selected range (mutation-tested in MarketingScreen.test.js). */}
            {canShowSocialHero && (
                <>
                    <GlassCard veil className="mkt-hero" data-testid="social-hero">
                        <HeroStat
                            label={t('total_interactions', {}, 'Interactions')}
                            value={fmtNum(totalInteractions)}
                        />
                    </GlassCard>

                    <div className="mkt-range">
                        <RangeBar value={preset} onChange={handleRangeChange} />
                    </div>

                    {insights && (
                        <div className="mkt-section">
                            <div className="mkt-section-title">
                                {t('date_range_label', {}, 'Metrics')}
                            </div>
                            <KpiGrid
                                kpis={insights.kpis}
                                connectedPlatforms={connectedMetaPlatforms}
                                t={t}
                            />
                        </div>
                    )}
                </>
            )}

            {/* Promotions hero — same denominator as the list below in every
                state: the list is never filtered, so total === rows.length
                always. */}
            {canShowPromotionsHero && (
                <GlassCard veil className="mkt-hero" data-testid="promotions-hero">
                    <HeroStat
                        label={t('promotions', {}, 'Promotions')}
                        value={promotions.length}
                        caption={t('n_active_promotions', { count: activePromoCount }, '{count} active')}
                    />
                </GlassCard>
            )}

            <PullToRefresh onRefresh={loadConnections}>
                <SurfaceStates
                    status={status}
                    onRetry={loadConnections}
                    emptyTitle={t('no_connections', {}, 'No accounts connected')}
                >
                    {segment === 'social' ? (
                        connections.length === 0 ? (
                            <SocialEmpty t={t} />
                        ) : (
                            <>
                                {/* Connection cards */}
                                <div className="mkt-section">
                                    <div className="mkt-section-title">
                                        {t('connections', {}, 'Connected Accounts')}
                                    </div>
                                    <div className="mkt-conn-list">
                                        {connections.map((conn) => (
                                            <ConnectionCard key={conn.external_account_id} conn={conn} t={t} />
                                        ))}
                                    </div>
                                </div>

                                {/* Top posts — count line + CondensedRow list */}
                                <div className="mkt-count-line" data-testid="posts-count-line">
                                    {t('n_posts', { count: topPosts.length }, '{count} Posts')}
                                </div>
                                <div className="mkt-section">
                                    {topPosts.length === 0 ? (
                                        <div className="mkt-empty">
                                            <div className="mkt-empty-title">
                                                {t('no_posts', {}, 'No posts in this period')}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="mkt-posts-list">
                                            {topPosts.map((post) => (
                                                <TopPostRow key={post.post_id} post={post} t={t} />
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Reviews (Google only) */}
                                {hasGoogle && reviews && (
                                    <div className="mkt-section">
                                        <div className="mkt-section-title">
                                            {t('reviews', {}, 'Reviews')}
                                        </div>
                                        <ReviewsSection reviewsData={reviews} t={t} />
                                    </div>
                                )}
                            </>
                        )
                    ) : (
                        <PromotionsBody promotions={promotions} t={t} />
                    )}
                </SurfaceStates>
            </PullToRefresh>
        </div>
    );
}
